import { Component, inject, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { BasePaginationController, ContextMenuClickInfo, ContextMenuItem, Explorer, IAddButtonOptions, IExplorerAttributes, IExplorerContentAttributes, IExplorerContentGraphAttributes, IExplorerHeaderAttributes, IExplorerNavAttributes, IListViewColHeader, IListViewItem, IPaginationReqMessage, MindMapLayout, OffCanvasIDsInDefaultLayout, Page, getOffCanvasIdFromLevel, OffCanvasPlacement, openOffCanvas, OptimaOffCanvasService, ToastService } from '@optima-stride-ai/optima-components';
import { OffCanvasCollectiveComponent } from '../../off-canvas/off-canvas-form/off-canvas-collective/off-canvas-collective.component';
import { OffcanvasService } from '@coreui/angular';
import { BizEntityFunctionTypes, SettingsService, IBizEntity, IBizEntityType, BizEntityTypeCategory } from '@optima-stride-ai/core';
import { Subscription } from 'rxjs';
import { IBenBizEntity } from '../../../models/interfaces/biz-entity.model';
import {  OffCanvasRightContentTypes, OffCanvasComponents, getOffCanvasComponentForBizEntityCategory } from '../../off-canvas/off-canvas-service/off-canvas-form.service';
import { AppStateService } from '../../state/app-state';
import { BizEntityChildrenStatusStoreService, AppTagService, EntityBottomOffCanvasService } from '@optima-stride-ai/optima-app-framework';
import { MeasuresStateStoreService } from '../../transaction(Measure)/measure-state-store/measures-state-store.service';
import { FormStates } from '../../utils/form/form-interfaces';
import { KpiStateStoreService } from '../KPI-state-stores/kpi-state-store.service';
import { IKPI } from '../kpi.model';
import { BizEntityStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { ContextMenuService } from '../../context-menu/context-menu';
import { LinkedEntityPickerService } from '../../linked-content-panel/linked-entity-picker/linked-entity-picker.service';
import { UtilService } from '../../utils/util.service';
import { ExplorerPaginationConfig } from '../../utils/explorer-pagination.config';
import { refreshExplorer, refreshExplorerNav } from '../../utils/explorer-nav-selection';
import { ExplorerReveal, ExplorerRoutes } from '../../utils/explorer-reveal';
import { getColHeadersForKPI, getListViewItemsForKPI } from '../kpi-list-view';
import { getColHeadersForBizEntity, getCombinedColHeadersForBizEntities, getListViewItemsForBizEntities, normalizeListItems } from '../../strategy/explorer-list-view';
import { filterBizEntitiesForApplicationExplorer, isExcludedFromApplicationExplorer } from '../../strategy/biz-entity-category-filters';
import { KpiProgressHistoryStateStoreService } from '../kpi-progress-history-state-store/kpi-progress-history-state-store.service';
import { RelationshipStateStoreService } from '@optima-stride-ai/optima-app-framework';

@Component({
  selector: 'app-kpi-explorer',
  imports: [Explorer],
  templateUrl: './kpi-explorer.component.html',
  styleUrl: './kpi-explorer.component.sass',
})
export class KpiExplorerComponent extends BasePaginationController<IBizEntity> implements OnInit, OnDestroy {

  @ViewChild('kpiExplorer') explorerComponent!: Explorer;
  @Input('isInModal') isInModal: boolean = false;
  @Input('functionTypeFilter') functionTypeFilter: BizEntityFunctionTypes | null = null; 
  @Input('onSelectionChange') onSelectionChange?: (data: IBizEntity | undefined) => void;
  subHeaderAttributes: IExplorerHeaderAttributes = {
    search: true,
    filter: false,
    add: true,
    view: true,
    sideBar: true,
    addWithOptions: true,
    addButtonOptions: [],
    breadCrumbToggle: true,
    nextDisable: false,
    previouseDisable: false,
    appliedFilterCount: 0,
    graphView: false,
  };

  explorerNavAttributes: IExplorerNavAttributes = {
    data: [],
    isNavigable:false,
  };

  explorerContentAttributes: IExplorerContentAttributes = {
    data: [],
    listData: {
      title: '',
      colHeaders: [],
      items: [],
    },
    currentSelectedItem: '',
  };

  // explorerContentGridAttributes: IExplorerContentGridAttributes = {
  //   explorerContentAttributes: this.explorerContentAttributes,
  //   visualizationData: {
  //     title: '',
  //     items: []
  //   },
  //   isInModal: this.isInModal
  // }
  
  explorerContentGraphAttributes: IExplorerContentGraphAttributes = {
    data: [],
    graphConfig: {
      // nodeContent: (d: any, i: any, arr: any, state: any) => ''
      cardLayout:[]
    },
    layout: MindMapLayout.Left,
    currentSelectedNodeId: '',
  };

  explorerAttributes: IExplorerAttributes = {
    data: [],
    subHeaderAttributes: this.subHeaderAttributes,
    explorerNavAttributes: this.explorerNavAttributes,
    explorerContentAttributes: this.explorerContentAttributes,
    // explorerGridAttributes: this.explorerContentGridAttributes,
    isInModal: this.isInModal,
    functionTypeFilter: this.functionTypeFilter,
  };
  dataRefreshedToken: string = '';
  private readonly appStateService: AppStateService = inject(AppStateService);
  private readonly offCanvasFormService = inject(OptimaOffCanvasService);
  private readonly offCanvasServiceRef = inject(OffcanvasService);
  private readonly entityBottomOffCanvasService = inject(EntityBottomOffCanvasService);
  private readonly contextMenuService = inject(ContextMenuService);
  private readonly linkedEntityPickerService = inject(LinkedEntityPickerService);
  private readonly settingsService = inject(SettingsService);
  private readonly bizEntityTypesStateStoresService:BizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
  private readonly kpiStateStoresService = inject(KpiStateStoreService);
  private readonly kpiProgressHistoryStateStoreService = inject(KpiProgressHistoryStateStoreService);
  private readonly bizEntityStateStoresService = inject (BizEntityStateStoreService);
  private readonly measureStateStoreService = inject(MeasuresStateStoreService);
  private readonly bizEntityChildrenStatusStoreService = inject(BizEntityChildrenStatusStoreService);
  private readonly relationshipStateStoreService = inject(RelationshipStateStoreService);
  private readonly appTagService = inject(AppTagService);
  private readonly utilService = inject(UtilService);

  kpiData: IKPI[] = [];
  strategyWithKPIData: IBizEntity[] = [];
  bizEntityTypes: IBizEntityType[] = [];
  strategyData: IBizEntity[] = [];
  private contextMenuItems: ContextMenuItem[] = [];
  
  // Subscriptions
  kpiStoreSubscription: Subscription | undefined;
  bizEntityStoreSubscription: Subscription | undefined;
  relationshipStoreSubscription: Subscription | undefined;
  private deleteCompletedSubscription: Subscription | undefined;
  
  // Track which data has been loaded at least once
  private dataLoadedOnce = {
    kpi: false,
    bizEntityTypes: false
  };
  private dataLoadingFlags = {
    bizEntities: false,
    kpi: false,
    bizEntityTypes: false
  };
  private allDataLoaded = false;
  private contentRequestSequence = 0;
  private readonly explorerReveal = new ExplorerReveal({
    route: ExplorerRoutes.Kpis,
    state: () => this.appStateService,
    explorer: () => this.explorerComponent,
    entities: () => this.explorerAttributes.data,
    fetch: (ids, ancestorsOf) => this.bizEntityStateStoresService.getState(
      this.bizEntityTypesStateStoresService, this.bizEntityChildrenStatusStoreService, ids, undefined, ancestorsOf),
    merge: entities => this.appendLoadedEntities(entities),
    openFolder: id => this.navItemClicked(id),
  });
  private readonly deletedEntityIds = new Set<string>();
  isLoading = signal<boolean>(true);
  readonly contentItemCountPerPage = ExplorerPaginationConfig.ContentItemCountPerPage;

  constructor(){
    super();
    this.contextMenuService.onMenuClicked = () => {
      setTimeout(() => {
        if(this.appStateService.offCanvasRightContentType === OffCanvasRightContentTypes.BizEntityForms){
          this.openBizEntityFormInOffCanvas()
        }
        else if(this.appStateService.offCanvasRightContentType === OffCanvasRightContentTypes.DetailForm)
        {
          this.openDetailOffCanvas();
        }
        else{
        this.openOthersInRightOffCanvas()
      }
      })
    }
    // this.contextMenuService.onPinToQuickActionClicked = () =>{
    //   setTimeout(() => {
    //     this.addToQuickActionClicked();
    //   })
    // }

  }

  // ===================== Lifecycle Hooks =====================
  
  ngOnInit(): void {
    this.subscribeToStates();
    if (!this.isInModal) {
      this.explorerReveal.watch();
    }
    this.fetchData(false);
  }

  ngOnDestroy(): void {
    this.unSubscribeFromStates();
    this.resetDataFlags();
    this.appStateService.selectedKPIAndMeasureCategory = null;
  }

  // ===================== Subscription Management =====================
  
  private subscribeToStates(): void {
    if (this.kpiStateStoresService.changed) {
      this.kpiStoreSubscription = this.kpiStateStoresService.changed.subscribe(() => {
        this.dataLoadedOnce.kpi = false;
        this.fetchData(true);
        this.ensureKPILoaded();
      });
    }
    if (this.bizEntityStateStoresService.changed) {
      this.bizEntityStoreSubscription = this.bizEntityStateStoresService.changed.subscribe(() => {
        this.fetchData(true);
      });
    }
    if (this.relationshipStateStoreService.changed) {
      this.relationshipStoreSubscription = this.relationshipStateStoreService.changed.subscribe(() => {
        if (this.appTagService.currentVersion() > this.appTagService.savedVersion()) {
          return;
        }
        // Reset all dataLoadedOnce flags so all data is fetched fresh from APIs
        // since relationships can link any entity to any other entity
        this.dataLoadedOnce = {
          kpi: false,
          bizEntityTypes: false
        };
        this.fetchData(true);
        this.ensureKPILoaded();
        this.ensureBizEntityTypesLoaded();
      });
    }
    this.deleteCompletedSubscription = this.contextMenuService.deleteCompletedEvent?.subscribe((entity) => {
      this.removeDeletedEntityFromExplorer(entity);
    });
  }

  private unSubscribeFromStates(): void {
    if (this.kpiStoreSubscription) {
      this.kpiStoreSubscription.unsubscribe();
    }
    if (this.bizEntityStoreSubscription) {
      this.bizEntityStoreSubscription.unsubscribe();
    }
    if (this.relationshipStoreSubscription) {
      this.relationshipStoreSubscription.unsubscribe();
    }
    this.deleteCompletedSubscription?.unsubscribe();
  }

  private resetDataFlags(): void {
    this.allDataLoaded = false;
    this.dataLoadingFlags = {
      bizEntities: false,
      kpi: false,
      bizEntityTypes: false
    };
    this.dataLoadedOnce = {
      kpi: false,
      bizEntityTypes: false
    };
    this.deletedEntityIds.clear();
  }

  // ===================== Data Loading & State Management =====================
  
  private fetchData(isStateStoreServiceStateChanged: boolean): void {
    this.isLoading.set(true);
    this.dataLoadingFlags.bizEntities = false;
    this.allDataLoaded = false;
    this.fetchBizEntityData(isStateStoreServiceStateChanged);
  }

  private fetchBizEntityData(isStateStoreServiceStateChanged: boolean): void {
    this.fetchKpiEntityPage(
      undefined,
      this.contentItemCountPerPage,
      0,
      [BizEntityTypeCategory.BenzaitenKPIStore]
    )
      .then(async (page) => {
        this.paginationBuffer.setInitialPage('', page);
        const ancestors = await this.fetchAncestorPath(page.items);
        this.strategyData = this.mergeEntities(this.strategyData, ancestors, page.items);
        this.dataLoadingFlags.bizEntities = true;
        
        // Preserve previously loaded data flags
        if (this.dataLoadedOnce.kpi) {
          this.dataLoadingFlags.kpi = true;
        }
        if (this.dataLoadedOnce.bizEntityTypes) {
          this.dataLoadingFlags.bizEntityTypes = true;
        }
        
        this.checkAndSetupExplorer(isStateStoreServiceStateChanged);
      })
      .catch((error) => {
        console.error('Error fetching biz entities:', error);
        this.dataLoadingFlags.bizEntities = true;
        this.checkAndSetupExplorer(isStateStoreServiceStateChanged);
        this.isLoading.set(false);
      });
  }

  /**
   * Lazy load KPI data only when needed
   * Returns a promise that resolves when data is loaded or if already cached
   */
  private ensureKPILoaded(ids?:string[]): Promise<void> {
    if (this.dataLoadedOnce.kpi && !ids?.length) {
      return Promise.resolve();
    }
    const loadedIds = new Set(this.kpiData.map((kpi) => kpi.id.toString()));
    const idsToLoad = ids?.filter((id) => !loadedIds.has(id.toString()));
    if (ids?.length && idsToLoad?.length === 0) {
      return Promise.resolve();
    }
    
    return this.kpiStateStoresService
      .getState(
        this.bizEntityTypesStateStoresService, 
        this.measureStateStoreService, 
        idsToLoad,
        this.kpiProgressHistoryStateStoreService
      )
      .then((data) => {
        if (data) {
          const kpisById = new Map(this.kpiData.map((kpi) => [kpi.id, kpi]));
          data.forEach((kpi) => kpisById.set(kpi.id, kpi));
          this.kpiData = Array.from(kpisById.values());
        }
        this.dataLoadedOnce.kpi = !ids?.length;
        this.dataLoadingFlags.kpi = true;
      })
      .catch((error) => {
        console.error('Error fetching KPI data:', error);
        this.dataLoadingFlags.kpi = true;
      });
  }

  /**
   * Lazy load BizEntityTypes only when needed
   * Returns a promise that resolves when data is loaded or if already cached
   */
  private ensureBizEntityTypesLoaded(): Promise<void> {
    if (this.dataLoadedOnce.bizEntityTypes) {
      return Promise.resolve();
    }
    
    return this.bizEntityTypesStateStoresService
      .getState()
      .then((data) => {
        if (data) {
          this.bizEntityTypes = data;
        }
        this.dataLoadedOnce.bizEntityTypes = true;
        this.dataLoadingFlags.bizEntityTypes = true;
      })
      .catch((error) => {
        console.error('Error fetching biz entity types:', error);
        this.dataLoadingFlags.bizEntityTypes = true;
      });
  }

  private checkAndSetupExplorer(isStateStoreServiceStateChanged: boolean): void {
    if (this.dataLoadingFlags.bizEntities && !this.allDataLoaded) {
      this.allDataLoaded = true;
      this.setupExplorerOnInit(isStateStoreServiceStateChanged);
    }
  }

  private setupExplorerOnInit(isStateStoreServiceStateChanged: boolean): void {
    this.ensureBizEntityTypesLoaded()
      .then(() => {
        this.mergeStrategyAndKPIData();
        this.defineExplorerAttributes(isStateStoreServiceStateChanged);
        this.getContextMenuItems();
        this.isLoading.set(false);
      })
      .catch((error) => {
        console.error('Error in setupExplorerOnInit:', error);
        this.isLoading.set(false);
      });
  }

  // ===================== Data Merging & Preparation =====================
  
  private mergeStrategyAndKPIData(): void {
    // if (this.isInModal && this.appStateService.selectedKPIAndMeasureCategory) {
    //   this.strategyWithKPIData = [...this.kpiData];
    //   this.strategyWithKPIData = this.utilService.findAncestors(this.strategyWithKPIData, this.strategyData);
    //   this.appStateService.selectedKPIAndMeasureCategory = null;
    // } else {
      const tenantBizEntity = this.strategyData.find(item => item.type.category === BizEntityTypeCategory.Tenant);
      const kpiStore = this.strategyData.filter((item) => item.type.category === BizEntityTypeCategory.BenzaitenKPIStore);
      let kpiStoreEntireHierarchy = this.utilService.findEntireHierarchy(kpiStore, this.strategyData);
      
      if (this.isInModal && this.functionTypeFilter !== null) {
        if (this.functionTypeFilter === BizEntityFunctionTypes.Structural) {
          kpiStoreEntireHierarchy = kpiStoreEntireHierarchy.filter(
            (item) => item.type.category !== BizEntityTypeCategory.BenzaitenKPI
          );
        } else {
          kpiStoreEntireHierarchy = kpiStoreEntireHierarchy.filter(
            (item) => item.type.functionType === this.functionTypeFilter
          );
          kpiStoreEntireHierarchy = this.utilService.findEntireHierarchy(kpiStore, this.strategyData);
        }
      }
      
      this.strategyWithKPIData = filterBizEntitiesForApplicationExplorer([...kpiStoreEntireHierarchy]);
      if(this.strategyWithKPIData.length === 0){
        this.strategyWithKPIData = tenantBizEntity ? [tenantBizEntity] : [];
      }
    // }
  }

  private extractItemChildren(itemId: string): IBizEntity[] {
    return this.utilService.extractItemChildren(itemId, this.strategyWithKPIData) as IBizEntity[];
  }

  // ===================== Sub-Header Management =====================
  
  private subHeaderOptions: IAddButtonOptions[] = [];

  onSubHeaderAddButtonClicked(): void {
    this.appStateService.selectedBizEntityForEdit = null;
    const entityType = this.bizEntityTypes.find(
      (item) => item.category === BizEntityTypeCategory.BenzaitenKPI && item.isSystemType
    );
    
    if (entityType) {
      this.appStateService.selectedBizEntityTypeForNewBizEntity = entityType;
    }
    
    const offCanvasComponent = getOffCanvasComponentForBizEntityCategory(entityType);
    if (offCanvasComponent !== null) {
      this.appStateService.entityFormState = FormStates.New;
      this.openBizEntityFormInOffCanvas();
    } else {
      console.error("No off-canvas component found for the selected biz entity type.");
    }
  }

  private prepareAddButtonOptions(): void {
    const kpiTypes = this.bizEntityTypes.filter(
      (item) => item.category === BizEntityTypeCategory.BenzaitenKPI
    );
    
    this.subHeaderOptions = kpiTypes.map((item) => ({
      id: item.id,
      label: item.name,
      icon: item.icon
    }));
  }

  onAddButtonOptionClick = (option: IAddButtonOptions): void => {
    this.appStateService.selectedBizEntityForEdit = null;
    this.appStateService.selectedKPIAndMeasureCategory = null;
    
    if (this.appStateService.entityFormState !== FormStates.Idle) {
      console.error("Entity form state is not idle. Cannot proceed with new entity creation.");
      return;
    }

    const entityType = this.bizEntityTypes.find(item => item.id === option.id);
    if (!entityType) {
      console.error("Invalid entity type.");
      return;
    }

    this.appStateService.selectedBizEntityTypeForNewBizEntity = entityType;

    const offCanvasComponent = getOffCanvasComponentForBizEntityCategory(entityType);
    if (offCanvasComponent !== null) {
      this.appStateService.entityFormState = FormStates.New;
      this.openBizEntityFormInOffCanvas();
    } else {
      console.error("No off-canvas component found for the selected biz entity type.");
    }
  }

  clickFilterButton = (): void => {
    // Filter functionality to be implemented
  }

  // ===================== Explorer Configuration =====================
  
  private defineExplorerAttributes(isStateStoreServiceStateChanged = false, data: IBizEntity[] = this.strategyWithKPIData): void {
    const revealId = isStateStoreServiceStateChanged || this.isInModal ? undefined : this.explorerReveal.takeInitialRequest();
    const selectedParentId = this.explorerNavAttributes.currentSelectedItem ||
      this.explorerContentAttributes.currentSelectedItem;
    this.explorerAttributes.data = data;
    this.explorerAttributes.isInModal = this.isInModal;
    this.explorerAttributes.subHeaderAttributes = this.subHeaderAttributes;
    this.explorerAttributes.explorerNavAttributes = this.explorerNavAttributes;
    this.explorerAttributes.explorerContentAttributes = this.explorerContentAttributes;
    
    this.defineSubHeaderAttributes()
      .then(() => {
        this.defineExplorerNavAttributes();
        return isStateStoreServiceStateChanged
          ? Promise.resolve()
          : this.defineExplorerContentAttributes();
      })
      .then(() => {
        refreshExplorer(this);
        
        if (isStateStoreServiceStateChanged) {
          return this.updateListData(selectedParentId || this.explorerAttributes.data[0]?.id || '');
        }
        return Promise.resolve();
      })
      .then(() => {
        const preSelectedItemId = this.appStateService.incomingBizEntitySelectionId;
        if (preSelectedItemId && this.explorerAttributes?.data?.length > 0) {
          this.explorerAttributes.preSelectedItemId = preSelectedItemId;
        }
        if (revealId) {
          void this.explorerReveal.reveal(revealId);
        }
      })
      .catch((error) => {
        console.error('Error defining explorer attributes:', error);
      });
  }

  private getContextMenuItems(): void {
    this.contextMenuItems = this.contextMenuService.getMenu();
    this.explorerAttributes.contextMenuItems = this.contextMenuItems;
  }

  private defineSubHeaderAttributes(): Promise<void> {
    return this.ensureBizEntityTypesLoaded()
      .then(() => {
        this.prepareAddButtonOptions();
        
        if (this.isInModal) {
          this.subHeaderAttributes.add = false;
          this.subHeaderAttributes.addWithOptions = false;
          this.subHeaderAttributes.filter = false;
          this.subHeaderAttributes.graphView = false;
        }
        
        this.subHeaderAttributes.addButtonOptions = this.subHeaderOptions;
      });
  }

  private defineExplorerNavAttributes(): void {
    this.explorerNavAttributes.data = [...this.explorerAttributes.data];
    
    if (!this.explorerNavAttributes.data.some(item => item.parentId === '')) {
      this.explorerNavAttributes.data = this.utilService.findAncestors(
        this.explorerNavAttributes.data, 
        this.strategyData
      );
    }
  }

  private defineExplorerContentAttributes(itemId?: string): Promise<void> {
    const requestSequence = ++this.contentRequestSequence;
    const targetItemId = itemId || this.explorerAttributes.data.find(
      (item) => item.parentId === '' || item.parentId === null
    )?.id || this.explorerAttributes.data[0]?.id || '';

    this.explorerNavAttributes.currentSelectedItem = targetItemId;
    this.explorerContentAttributes.currentSelectedItem = targetItemId;

    return this.fetchPaginationPage(targetItemId, this.contentItemCountPerPage, 0)
      .then(async (page) => {
        await this.explorerReveal.extendPage(targetItemId, page,
          offset => this.fetchPaginationPage(targetItemId, this.contentItemCountPerPage, offset),
          () => requestSequence === this.contentRequestSequence);
        if (requestSequence !== this.contentRequestSequence) return;
        this.paginationBuffer.setInitialPage(targetItemId, page);
        this.appendLoadedEntities(page.items);
          return this.renderContentChildren(targetItemId, page.items, requestSequence);
      });
  }

        private renderContentChildren(targetItemId: string, children: IBizEntity[], requestSequence?: number): Promise<void> {
    this.explorerContentAttributes.data = children;

    const flags = {
      containsStructuralBizEntity: children.some(e => e.type.category === BizEntityTypeCategory.Structural || e.type.category === BizEntityTypeCategory.BenzaitenObjective || e.type.functionType === BizEntityFunctionTypes.Structural),
      containsDeliverableBizEntity: children.some(e => e.type.category === BizEntityTypeCategory.BenzaitenDeliverable),
      containsKPIBizEntity: children.some(e => e.type.category === BizEntityTypeCategory.BenzaitenKPI),
      containsMeasureBizEntity: children.some(e => e.type.category === BizEntityTypeCategory.BenzaitenMeasure),
    };

    let types:Set<BizEntityTypeCategory> = new Set();
    children.forEach(child => types.add(child.type.category));

    const colHeaders = getCombinedColHeadersForBizEntities(
      flags.containsStructuralBizEntity,
      flags.containsDeliverableBizEntity,
      flags.containsKPIBizEntity,
      flags.containsMeasureBizEntity
    );

    if(children.some((item) => item.type.category === BizEntityTypeCategory.BenzaitenKPI)){
      const kpiIds = children
        .filter((item) => item.type.category === BizEntityTypeCategory.BenzaitenKPI)
        .map((item) => item.id);
      return this.ensureKPILoaded(kpiIds)
        .then(() => {
          if (requestSequence !== undefined && requestSequence !== this.contentRequestSequence) return;
          this.explorerContentAttributes.listData = {
            title: '',
            colHeaders: colHeaders,
            items: this.prepareListData(targetItemId, colHeaders) as unknown as IListViewItem<IBizEntity>[],
            hasTableAutoInsertedRow: true
          };
          this.explorerComponent?.interceptListActions(this.explorerContentAttributes.listData);
        });
    }
    else{
      this.explorerContentAttributes.listData = {
        title: '',
        colHeaders: colHeaders,
        items: this.prepareListData(targetItemId, colHeaders) as unknown as IListViewItem<IBizEntity>[],
        hasTableAutoInsertedRow: true
      };
      this.explorerComponent?.interceptListActions(this.explorerContentAttributes.listData);
      return Promise.resolve();
    } 
    // this.prepareListData(targetItemId)
    //   .then((items) => {
    //     const isKPICategory = this.explorerContentAttributes.data.some(
    //       (item) => item.type.category === BizEntityTypeCategory.BenzaitenKPI
    //     );
        
    //     this.explorerContentAttributes.listData = {
    //       title: '',
    //       // colHeaders: isKPICategory ? getColHeadersForKPI() : getColHeadersForBizEntity(),
    //       colHeaders: colHeaders,
    //       items: items,
    //       hasTableAutoInsertedRow: true
    //     };
        
    //     this.explorerComponent?.interceptListActions(this.explorerContentAttributes.listData);
    //   });
    
  }
  

  // ===================== List Data Preparation =====================
  
  private updateListData(itemId: string): Promise<void> {
    this.explorerNavAttributes.currentSelectedItem = itemId;
    this.explorerContentAttributes.currentSelectedItem = itemId;
    return this.defineExplorerContentAttributes(itemId);
    // return this.prepareListData(itemId)
    //   .then((items) => {
    //     this.explorerContentAttributes.listData.items = items;
    //     return this.defineExplorerContentAttributes(itemId);
    //   })
    //   .catch((error) => {
    //     console.error('Error updating list data:', error);
    //   });
  }

  override async onPaginationRequest(request: IPaginationReqMessage): Promise<void> {
    const isNavRequest = request.requesterId.startsWith('explorer-nav-');
    // Keep the nav tree's own loading state isolated from content-pane pagination requests,
    // so scrolling the list doesn't blank the hierarchy with a full-tree skeleton.
    this.explorerNavAttributes.isLoading = isNavRequest;
    try {
      await super.onPaginationRequest(request);
    } finally {
      this.explorerNavAttributes.isLoading = false;
    }
  }

  protected override resolvePaginationParentId(request: IPaginationReqMessage): string {
    return request.parentId || this.explorerContentAttributes.currentSelectedItem || '';
  }

  protected override setPaginationLoading(isLoading: boolean): void {
    this.isLoading.set(isLoading);
  }

  protected override async fetchPaginationPage(
    parentId: string | undefined,
    limit: number,
    offset: number
  ): Promise<Page<IBizEntity>> {
    if (parentId && !this.isInKpiStoreSubtree(parentId)) {
      const pathChildren = this.strategyData.filter((entity) =>
        entity.parentId === parentId && !this.deletedEntityIds.has(entity.id.toString())
      );
      return {
        items: pathChildren.slice(offset, offset + limit),
        totalCount: pathChildren.length,
      };
    }
    return this.fetchKpiEntityPage(parentId, limit, offset);
  }

  private async fetchKpiEntityPage(
    parentId: string | undefined,
    limit: number,
    offset: number,
    categoryTypes: BizEntityTypeCategory[] = this.getExplorerCategories()
  ): Promise<Page<IBizEntity>> {
    const data = await this.bizEntityStateStoresService.getState(
      this.bizEntityTypesStateStoresService,
      this.bizEntityChildrenStatusStoreService,
      undefined,
      parentId,
      undefined,
      undefined,
      categoryTypes,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      limit,
      offset
    );
    const items = filterBizEntitiesForApplicationExplorer(data ?? [])
      .filter(entity => !this.deletedEntityIds.has(entity.id.toString()));
    const reportedTotalCount = (data?.[0] as (IBizEntity & { totalRecords?: unknown }) | undefined)?.totalRecords;
    return {
      items,
      totalCount: typeof reportedTotalCount === 'number' ? reportedTotalCount : items.length,
    };
  }

  protected override onNavPaginationChildrenLoaded(
    _parentId: string,
    appendedItems: IBizEntity[],
    _knownEmptyFolderIds: string[]
  ): void {
    this.appendLoadedEntities(appendedItems);
  }

  protected override async onContentPaginationChildrenLoaded(
    parentId: string,
    appendedItems: IBizEntity[]
  ): Promise<void> {
    if (parentId !== this.explorerContentAttributes.currentSelectedItem) {
      return;
    }
    this.appendLoadedEntities(appendedItems);
    await this.renderContentChildren(parentId, this.paginationBuffer.getItems(parentId));
  }

  private appendLoadedEntities(entities: IBizEntity[]): void {
    entities = entities.filter(entity => !this.deletedEntityIds.has(entity.id.toString()));
    if (entities.length === 0) return;
    this.strategyData = this.mergeEntities(this.strategyData, entities);
    this.mergeStrategyAndKPIData();
    this.explorerAttributes.data = this.strategyWithKPIData;
    this.explorerNavAttributes.data = [...this.strategyWithKPIData];
    refreshExplorerNav(this.explorerComponent);
  }

  private async fetchAncestorPath(entities: IBizEntity[]): Promise<IBizEntity[]> {
    const ancestorPages = await Promise.all(entities.map((entity) =>
      this.bizEntityStateStoresService.getState(
        this.bizEntityTypesStateStoresService,
        this.bizEntityChildrenStatusStoreService,
        undefined,
        undefined,
        entity.id
      )
    ));
    return ancestorPages.flatMap((page) => page ?? []);
  }

  private mergeEntities(...groups: IBizEntity[][]): IBizEntity[] {
    const entitiesById = new Map<string, IBizEntity>();
    groups.flat().filter(entity => !this.deletedEntityIds.has(entity.id.toString()))
      .forEach((entity) => entitiesById.set(entity.id, entity));
    return Array.from(entitiesById.values());
  }

  private removeDeletedEntityFromExplorer(entity: IBizEntity): void {
    const deletedId = entity.id.toString();
    this.deletedEntityIds.add(deletedId);
    const keepEntity = <T extends IBizEntity>(items: T[]): T[] =>
      items.filter(item => item.id.toString() !== deletedId);

    this.strategyData = keepEntity(this.strategyData);
    this.strategyWithKPIData = keepEntity(this.strategyWithKPIData);
    this.kpiData = keepEntity(this.kpiData);
    this.explorerAttributes.data = keepEntity(this.explorerAttributes.data);
    this.explorerNavAttributes.data = keepEntity(this.explorerNavAttributes.data);
    this.explorerContentAttributes.data = keepEntity(this.explorerContentAttributes.data);
    this.explorerContentGraphAttributes.data = keepEntity(this.explorerContentGraphAttributes.data);
    this.explorerContentAttributes.listData.items = this.explorerContentAttributes.listData.items
      .filter(item => String(item.id) !== deletedId);

    const parentId = entity.parentId?.toString() ?? '';
    const bufferedItems = this.paginationBuffer.getItems(parentId);
    if (bufferedItems.some(item => item.id.toString() === deletedId)) {
      const totalCount = this.paginationBuffer.getTotalCount(parentId) ?? bufferedItems.length;
      this.paginationBuffer.setInitialPage(parentId, {
        items: keepEntity(bufferedItems),
        totalCount: Math.max(0, totalCount - 1),
      });
    }

    refreshExplorerNav(this.explorerComponent);
    this.explorerComponent?.interceptListActions(this.explorerContentAttributes.listData);
  }

  private isInKpiStoreSubtree(entityId: string): boolean {
    const kpiStore = this.strategyData.find(
      (entity) => entity.type.category === BizEntityTypeCategory.BenzaitenKPIStore
    );
    let current = this.strategyData.find((entity) => entity.id === entityId);
    const visited = new Set<string>();
    while (current && !visited.has(current.id)) {
      if (current.id === kpiStore?.id) return true;
      visited.add(current.id);
      current = this.strategyData.find((entity) => entity.id === current?.parentId);
    }
    return false;
  }

  private getExplorerCategories(): BizEntityTypeCategory[] {
    return Object.values(BizEntityTypeCategory).filter(
      (category): category is BizEntityTypeCategory =>
        typeof category === 'number' && !isExcludedFromApplicationExplorer(category)
    );
  }

  private prepareListData(itemId: string, colHeaders?:IListViewColHeader[]): IListViewItem<IBizEntity>[] {
    const selectedEntityChildren = this.extractItemChildren(itemId);
    let ids: string[] = [];
    selectedEntityChildren.forEach((child) => {
      ids.push(child.id);
    });

    let items: IListViewItem<IBizEntity>[] = [];
    const itemMap = new Map(items.map(i => [i.id, i]));

    const bizEntites = getListViewItemsForBizEntities(
        selectedEntityChildren, 
        this.menuButtonClicked, 
        this.itemClicked, 
        this.selectClickedItem, 
        this.isInModal,
        undefined,
        this.linkedEntityPickerService
      )
    
    bizEntites.forEach((bizEntity) => {
      itemMap.set(bizEntity.id, bizEntity);
    });

    if (selectedEntityChildren.some((item) => item.type.category === BizEntityTypeCategory.BenzaitenKPI)) {
     
          let kpiChildren = this.kpiData.filter((item) => item.parentId === itemId);
          if(this.appStateService.selectedKPIAndMeasureCategory)
          {
            kpiChildren = kpiChildren.filter((item) => item.category === this.appStateService.selectedKPIAndMeasureCategory);
            // console.log('Filtered KPI Children for Category:', this.appStateService.selectedKPIAndMeasureCategory, kpiChildren);
          }
          
          const KPIList =  getListViewItemsForKPI(
            kpiChildren, 
            this.menuButtonClicked, 
            this.itemClicked, 
            this.selectClickedItem, 
            this.isInModal, 
            this.onMeasureValueClicked,
            undefined,
            this.linkedEntityPickerService
          );
          KPIList.forEach((kpiItem) => {
            itemMap.set(kpiItem.id, kpiItem);
          });

          // Remove KPI items of non-matching categories that were added via strategyWithKPIData
          if (this.appStateService.selectedKPIAndMeasureCategory && this.isInModal) {
            const filteredKpiIds = new Set(kpiChildren.map(k => k.id));
            selectedEntityChildren
              .filter(child => child.type.category === BizEntityTypeCategory.BenzaitenKPI && !filteredKpiIds.has(child.id))
              .forEach(child => itemMap.delete(child.id));
          }
        };
    
    
    items = Array.from(itemMap.values());
    return colHeaders
      ? normalizeListItems(items, colHeaders)
      : items;
  }
  
  _selectedKPI: IKPI | undefined;

  private menuButtonClicked = (item: IListViewItem<IBizEntity> | undefined, event?:Event) => {}

  selectClickedItem = (itemId: string) => {
    let item:IBizEntity | IKPI | undefined;
    // if(this.appStateService.selectedKPIAndMeasureCategory){
    //   item = this.kpiData.find(entity => entity.id === itemId);
    // } else{
    //   item = this.strategyWithKPIData.find(entity => entity.id === itemId);
    // }
    let isItemAKPI = this.kpiData.find(entity => entity.id === itemId);
    if(isItemAKPI){
      item = isItemAKPI;
    }else{
      item = this.strategyWithKPIData.find(entity => entity.id === itemId);
    }
    if(this.isInModal){
      this._selectedKPI = item as IKPI;
      this.onSelectionChange?.(this._selectedKPI);
    }
  }
  get data(): IKPI | undefined {
    return this._selectedKPI;
  }
  
  navItemClicked = (itemId: string) => {
    this.isLoading.set(true);
    this.explorerNavAttributes.isLoading = false;
    
    let item:IBizEntity | IKPI | undefined;
    // if(this.appStateService.selectedKPIAndMeasureCategory){
    //   item = this.kpiData.find(entity => entity.id === itemId);
    // } else{
    //   item = this.strategyWithKPIData.find(entity => entity.id === itemId);
    // }
    let isItemAKPI = this.kpiData.find(entity => entity.id === itemId);
    if(isItemAKPI){
      item = isItemAKPI;
    }else{
      item = this.strategyWithKPIData.find(entity => entity.id === itemId);
    }
    if(this.isInModal){
      this._selectedKPI = item as IKPI;
      this.onSelectionChange?.(this._selectedKPI);
    }
    
    this.updateListData(itemId)
      .then(() => {
        this.isLoading.set(false);
        this.explorerNavAttributes.isLoading = false;
      })
      .catch((error) => {
        console.error('Error updating list data:', error);
        this.isLoading.set(false);
        this.explorerNavAttributes.isLoading = false;
      });
    this.resetIncomingBizEntitySelectionId();
  }

  itemClicked = (itemId: string, actionOnOperationalElementClicked:boolean=true) => {
    this.subHeaderAttributes.previouseDisable = false;
    let item = this.explorerAttributes.data.find(entity => entity.id === itemId);
    if (this.extractItemChildren(itemId).length === 0 && actionOnOperationalElementClicked) {
      if(!this.isInModal){
        if(item?.type.functionType === BizEntityFunctionTypes.Operational){
          // this.onActionButtonClicked(itemId);
          this.openDetailOffCanvas(itemId)
        }
        else{
          this.isLoading.set(true);
          
          this.updateListData(itemId)
            .then(() => {
              this.isLoading.set(false);
            })
            .catch((error) => {
              console.error('Error updating list data:', error);
              this.isLoading.set(false);
            });
        }
      }
      return
    }
    
    this.isLoading.set(true);
    
    this.updateListData(itemId)
      .then(() => {
        this.isLoading.set(false);
      })
      .catch((error) => {
        console.error('Error updating list data:', error);
        this.isLoading.set(false);
      });
    this.resetIncomingBizEntitySelectionId();
  }

  openDetailOffCanvas(itemId?: string){
    if(!itemId)
    {
      itemId = this.appStateService.selectedBizEntityForEdit?.id;
    }
    if(this.isInModal) return;
    // Prefer the KPI-enriched record (has .measure link); fall back to the selected entity so View works
    // for Groups / non-KPI nodes that are visible in this explorer's tree but not in kpiData.
    let entity: IBizEntity | undefined = this.kpiData.find(entity => entity.id === itemId);
    if (!entity) {
      entity = this.appStateService.selectedBizEntityForEdit ?? undefined;
    }
    if (!entity) {
      this.appStateService.entityFormState = FormStates.Idle;
      return;
    }
    this.appStateService.offCanvasDetailActiveFor = entity;
    this.entityBottomOffCanvasService.open(entity);
  }

  onMeasureValueClicked = (itemId:string) => {
    // console.log('Measure value clicked');
    // this.itemClicked(itemId);
    let kpi = this.kpiData.find(entity => entity.id === itemId);
    if (kpi && kpi.measure) {
      let measure = kpi.measure[0];
      // this.appStateService.setSelectedMeasure(measure);
      this.appStateService.offCanvasDetailActiveFor = measure;
      if(!this.isInModal) {
        this.entityBottomOffCanvasService.open(measure);
      }
    }
  }

  processContentContextMenuClicked(event: ContextMenuClickInfo): void {
      const { id: itemId, event: domEvent } = event;
      if (!itemId) {
        console.error('Invalid item ID.');
        return;
      }
  
      // let bizEntityItem = this.benBizEntityMockDataService.getItem(itemId!, getTenantIdInString(this.tenantService.organization.tenantId), this.mockAPIResponseService);
      // Prefer the KPI-enriched record (has .progressStatus/.measure) over explorerAttributes.data's
      // generic structural entity - same lookup order as openDetailOffCanvas, so the context-menu
      // "View" action shows the same chart data as a direct double-click.
      let item = this.kpiData.find(entity => entity.id === itemId) ?? this.explorerAttributes.data.find(entity => entity.id === itemId);
      // this.explorerContentAttributes.contentContextMenu = this.planTreeContextMenuService.getMenu(this.tenantService.organization.tenantId);
      // this.planTreeContextMenuService.contextMenuEvent.emit(hierarchyItem);
      this.appStateService.selectedBizEntityForEdit = item! as IKPI;
      const entityType = this.bizEntityTypes.find((item) => item.category === BizEntityTypeCategory.BenzaitenKPI);
      if(entityType)
      {
        this.appStateService.selectedBizEntityTypeForNewBizEntity = entityType;
      }
      this.contextMenuService.bizEnitityEvent.emit(item);
      // console.log('KPI Explorer Context Menu Clicked Item:', item);
      if(item && item.type)
      {
        this.updateContextMenuList(item, [...this.contextMenuItems]);
      }
    }

    updateContextMenuList(entity:IBizEntity, menuItems: ContextMenuItem[])
    {
      const list = this.contextMenuService.getEntityListBasedOnType(entity, menuItems);
      this.explorerAttributes.contextMenuItems = list;

      // When the "Cross App" option is present, fetch the related entities for each
      // cross-app relationship target type asynchronously and reassign a NEW array
      // reference so an already-open context menu re-renders with the populated
      // sub-items.
      this.contextMenuService.populateCrossAppSubItems(entity, list).then(updatedList => {
        if (updatedList) {
          this.explorerAttributes.contextMenuItems = updatedList;
        }
      });
    }
    private mapHeadersToColHeaders(data:{key: string;label: string;type: string;}[]): IListViewColHeader[] {
      let headers = data;
      if (headers) {
        this.explorerContentAttributes.listData.colHeaders = headers.map(header => ({
          key: header.key,
          label: header.label,
        }));
      }
      return headers;
    }
  
    // ===================== Off-Canvas & Actions =====================
  
    onActionButtonClicked(itemId:string) {
      //CAUTION: Following is temporary arrangement to set selected biz entity for edit because plan element is a not a biz entity type node.
      this.appStateService.entityFormState = FormStates.Edit;
      let item = this.strategyWithKPIData.find(entity => entity.id === itemId);
      if(!item){

      }
      this.appStateService.selectedBizEntityForEdit = item! as IBenBizEntity;
      this.appStateService.selectedBizEntityTypeForNewBizEntity = this.appStateService.selectedBizEntityForEdit.type;
      this.openBizEntityFormInOffCanvas();
  }

  private openBizEntityFormInOffCanvas = () => {
    let comp: OffCanvasComponents | null = null;
    let shouldOpen = false;
    if (this.appStateService.entityFormState == FormStates.New) {
      // console.log("in constructor > For new, Selected biz entity type - " + this.appStateService.selectedBizEntityTypeForNewBizEntity?.name)
      const t = this.appStateService.selectedBizEntityTypeForNewBizEntity;
      comp = getOffCanvasComponentForBizEntityCategory(t ? t : undefined);
      if (comp !== null) {
        // console.log(`[NEW] For BizEntity type ${t?.name}, component selected is - ` + getKeyOfEnumValue(comp, OffCanvasComponents));
        shouldOpen = true;
      } else {
        console.error("[NEW] No off-canvas component found for the selected biz entity type.");
      }
    } else if (this.appStateService.entityFormState == FormStates.Edit && this.appStateService.selectedBizEntityForEdit !== null) {
      const t = this.appStateService.selectedBizEntityForEdit.type;
      comp = getOffCanvasComponentForBizEntityCategory(t ? t : undefined);
      if (comp !== null) {
        // console.log(`[EDIT] For BizEntity type ${t?.name}, component selected is - ` + getKeyOfEnumValue(comp, OffCanvasComponents));
        shouldOpen = true;
      } else {
        console.error(`[EDIT] No off-canvas component found for ${t?.name}`);
      }
    }
    if (shouldOpen && comp !== null) {
      const level = this.offCanvasFormService.currentOffCanvas?.level ?? 0;
      openOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, comp, getOffCanvasIdFromLevel(level + 1), level + 1, true); //last param is important
      // console.log('Edit/Views',FormStates[this.appStateService.entityFormState]);
    } else {
      this.appStateService.entityFormState = FormStates.Idle;
    }
  }

  private openOthersInRightOffCanvas = () => {
    if(this.appStateService.offCanvasRightContentType === OffCanvasRightContentTypes.Others){
      const level = this.offCanvasFormService.currentOffCanvas?.level ?? 0;
      openOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, this.appStateService.offCanvasRightComponent, getOffCanvasIdFromLevel(level + 1), level + 1, false);
    }
  }
  
  addToQuickActionClicked() {
    // console.log('KPI add to quick Action Called', this.appStateService.selectedBizEntityForEdit);
  }

  resetIncomingBizEntitySelectionId() {
    this.appStateService.incomingBizEntitySelectionId = null;
  }
}